import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { RefreshCw, X, ChevronLeft, ChevronRight, AlertTriangle } from 'lucide-react';
import { SkeletonRows, NoResults, ErrorBanner, PermissionDenied } from './shell/carbonStates';

// ============================================================================
// CHA-652 — Orders → Pre Allocation. Read-only view of the latest CHA-644 snapshot.
// The tenant is resolved server-side from the caller's scope; the prop is only
// forwarded so a platform admin can name the tenant. Counters, filters and
// paging all run in the callable, so the browser never holds the full ~43K-line day.
// ============================================================================

const PAGE_SIZE = 50;
const SEARCH_DEBOUNCE_MS = 300;
// The InMocean job runs weekdays at 14:40 ET; by 15:00 that day's snapshot should exist.
const SNAPSHOT_READY_HOUR_NY = 15;

// The ticket's drawer explains each result from stored evidence only. The snapshot keeps no
// start or cancel date, so these say what the status means rather than re-deriving it.
export const STATUS_EXPLANATIONS = {
  'in stock': 'Fully covered from on-hand stock; ready after handling time.',
  'on time': 'Fully covered; projected ready on or before the order start date.',
  'late before cancel': 'Fully covered; projected ready after the start date but by the cancel date.',
  'misses cancel': 'Fully covered, but projected ready after the cancel date.',
  'cancel passed': 'The cancel date had already passed when the snapshot ran.',
  partial: 'Only part of the quantity is covered; the rest is short.',
  'no supply': 'No stock or production was available for this line.',
  'no warehouse': 'The line has no warehouse, so it was not allocated.',
  'picked / routed': 'Already picked; the status shown is the routing status from the ERP.',
};

const EMPTY_FILTERS = { customer: '', order: '', style: '', warehouse: '', division: '', status: '', startFrom: '', startTo: '', attention: false };

function nyParts(date) {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/New_York',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    hourCycle: 'h23',
    weekday: 'short',
  }).formatToParts(date);
  const get = (type) => parts.find((p) => p.type === type).value;
  return { date: `${get('year')}-${get('month')}-${get('day')}`, hour: Number(get('hour')), weekday: get('weekday') };
}

function previousDay(isoDate) {
  const d = new Date(`${isoDate}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() - 1);
  return d.toISOString().slice(0, 10);
}

function isWeekend(isoDate) {
  const day = new Date(`${isoDate}T12:00:00Z`).getUTCDay();
  return day === 0 || day === 6;
}

// The newest snapshot date that should exist at `now`: today after the run window on a weekday,
// otherwise the previous weekday.
export function expectedSnapshotDate(now) {
  const { date, hour } = nyParts(now);
  let candidate = date;
  if (isWeekend(candidate) || hour < SNAPSHOT_READY_HOUR_NY) candidate = previousDay(candidate);
  while (isWeekend(candidate)) candidate = previousDay(candidate);
  return candidate;
}

export function isSnapshotStale(snapshotDate, now = new Date()) {
  if (!snapshotDate) return false;
  return snapshotDate < expectedSnapshotDate(now);
}

const fmtNum = (v) => (v === null || v === undefined ? '—' : Number(v).toLocaleString('en-US', { maximumFractionDigits: 0 }));
const fmtMoney = (v) => (v === null || v === undefined ? '—' : `$${Number(v).toLocaleString('en-US', { maximumFractionDigits: 0 })}`);
const fmtPct = (v) => (v === null || v === undefined || !Number.isFinite(Number(v)) ? '—' : `${(Number(v) * 100).toFixed(1)}%`);
const fmtTime = (iso) => (iso ? new Date(iso).toLocaleString('en-US', { timeZone: 'America/New_York', dateStyle: 'medium', timeStyle: 'short' }) + ' ET' : '—');

function supplySummary(sources) {
  if (!sources || sources.length === 0) return '—';
  return sources
    .map((s) => (s.type === 'stock' ? `Stock ${fmtNum(s.qty)}` : `PO ${s.po || '?'} ${s.stage || ''} ${fmtNum(s.qty)}`.replace(/\s+/g, ' ').trim()))
    .join(' + ');
}

function waterfallKeyOf(f) {
  return JSON.stringify([f.customer, f.order, f.style, f.warehouse, f.division, f.startFrom, f.startTo, f.attention]);
}

function useDebounced(value, ms) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return debounced;
}

const inputCls = 'px-2 py-1.5 border border-carbon-border font-plex-mono text-xs bg-carbon-white focus:outline-none focus:border-carbon-blue-60 min-w-0';

export default function PreAllocationDashboard({ tenantId }) {
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [offset, setOffset] = useState(0);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [denied, setDenied] = useState(false);
  const [selected, setSelected] = useState(null);
  // CHA-655 (Ron): outlook lines left out of the total. View-only and kept while filters change, so a
  // choice like "without misses cancel" carries from one range or division to the next.
  const [excludedSteps, setExcludedSteps] = useState(() => new Set());
  const requestSeq = useRef(0);

  const customer = useDebounced(filters.customer, SEARCH_DEBOUNCE_MS);
  const order = useDebounced(filters.order, SEARCH_DEBOUNCE_MS);
  const style = useDebounced(filters.style, SEARCH_DEBOUNCE_MS);
  const { warehouse, division, status, startFrom, startTo, attention } = filters;
  // A typed date can bypass the pickers' min/max. An inverted range is never sent (the server would reject
  // it and the error would replace the page); the page says so in place of the lines instead.
  const rangeInverted = Boolean(startFrom && startTo && startFrom > startTo);

  useLayoutEffect(() => {
    requestSeq.current += 1;
    setData(null);
    setSelected(null);
    setDenied(false);
  }, [tenantId]);

  const fetchData = useCallback(async () => {
    if (!tenantId) {
      requestSeq.current += 1;
      setData(null);
      setSelected(null);
      setLoading(false);
      setError('No tenant is selected for this session.');
      return;
    }
    const seq = ++requestSeq.current;
    if (rangeInverted) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    setDenied(false);
    try {
      const call = httpsCallable(getFunctions(), 'getPreAllocationSnapshot', { timeout: 60000 });
      const result = await call({
        tenantId,
        filters: { customer, order, style, warehouse, division, status, startFrom, startTo, attention },
        limit: PAGE_SIZE,
        offset,
      });
      // The waterfall ignores Status (Ron ruling, PR #485), so it is keyed by every other filter it was built for.
      if (seq === requestSeq.current) setData({ ...result.data, waterfallKey: waterfallKeyOf({ customer, order, style, warehouse, division, startFrom, startTo, attention }) });
    } catch (err) {
      if (seq !== requestSeq.current) return;
      if (err && (err.code === 'functions/permission-denied' || err.code === 'permission-denied')) setDenied(true);
      else setError(err && err.message ? err.message : 'The snapshot could not be loaded.');
    } finally {
      if (seq === requestSeq.current) setLoading(false);
    }
  }, [tenantId, customer, order, style, warehouse, division, status, startFrom, startTo, rangeInverted, attention, offset]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const setFilter = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setOffset(0);
  };
  const clearFilters = () => {
    setFilters(EMPTY_FILTERS);
    setOffset(0);
  };
  const hasFilters = Object.entries(filters).some(([k, v]) => v !== EMPTY_FILTERS[k]);

  if (denied) return <PermissionDenied requiredRole="Orders" grantedBy="your tenant admin" />;

  const snapshotDate = data && data.snapshotDate;
  const stale = isSnapshotStale(snapshotDate);
  const total = data ? data.total : 0;
  const rows = (data && data.rows) || [];
  const counters = (data && data.counters) || [];
  const counterTotal = counters.reduce((sum, c) => sum + c.lines, 0);
  // Only show a waterfall built for the filters now applied. While a new request is pending, or after it
  // fails, the previous response's waterfall describes other filters and stays hidden.
  const currentWaterfallKey = waterfallKeyOf({ customer, order, style, warehouse, division, startFrom, startTo, attention });
  const waterfall = data && data.waterfall && startTo && data.waterfall.startTo === startTo && data.waterfallKey === currentWaterfallKey ? data.waterfall : null;

  return (
    <div className="space-y-4 font-plex text-carbon-ink">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="font-plex-mono text-[11px] text-carbon-gray-50 flex flex-wrap gap-x-4 gap-y-1">
          <span>SNAPSHOT <span className="text-carbon-ink" data-testid="snapshot-date">{snapshotDate || '—'}</span></span>
          <span>COMPUTED <span className="text-carbon-gray-70">{fmtTime(data && data.computedAt)}</span></span>
          <span>SOURCES AFTER <span className="text-carbon-gray-70">{fmtTime(data && data.sourceFreshAfter)}</span></span>
        </div>
        <button
          type="button"
          onClick={fetchData}
          disabled={loading}
          className="flex items-center gap-2 px-3.5 py-2 text-[12.5px] font-medium bg-transparent border border-carbon-gray-30 text-carbon-ink hover:bg-carbon-gray-10 disabled:opacity-50 transition-colors"
        >
          <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
          Refresh
        </button>
      </div>

      {error ? <ErrorBanner what="Pre-allocation snapshot failed to load" staleNote={error} onRetry={fetchData} /> : null}

      {stale ? (
        <div role="status" className="relative bg-carbon-white border border-carbon-border px-[18px] py-3 flex items-start gap-3">
          <span className="absolute left-0 top-0 bottom-0 w-[3px] bg-carbon-orange-40" />
          <AlertTriangle size={15} className="text-carbon-gray-70 mt-0.5 flex-shrink-0" />
          <div className="text-[13px]">
            <span className="font-semibold">Snapshot is stale.</span> Latest is {snapshotDate}; {expectedSnapshotDate(new Date())} was expected.
            The daily run may have failed its source check. Figures below are from {snapshotDate}.
          </div>
        </div>
      ) : null}

      {!loading && !error && data && !snapshotDate ? (
        <div className="bg-carbon-white border border-carbon-border px-6 py-10 text-center" data-testid="empty-state">
          <div className="text-sm font-semibold">No pre-allocation snapshot yet</div>
          <div className="text-xs text-carbon-gray-50 mt-1">
            The daily calculation has not produced a snapshot for this tenant. This page fills in after its first successful run.
          </div>
        </div>
      ) : null}

      {snapshotDate ? (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 xl:grid-cols-9 gap-px bg-carbon-border border border-carbon-border" data-testid="counters" data-counter-total={counterTotal}>
            {counters.map((c) => {
              const active = status === c.status;
              return (
                <button
                  key={c.status}
                  type="button"
                  aria-pressed={active}
                  onClick={() => setFilter('status', active ? '' : c.status)}
                  className={`text-left px-3 py-2.5 transition-colors ${active ? 'bg-carbon-ink text-carbon-white' : 'bg-carbon-white hover:bg-carbon-gray-10'}`}
                >
                  <div className={`font-plex-mono text-[9.5px] tracking-[0.1em] uppercase ${active ? 'text-carbon-gray-30' : 'text-carbon-gray-50'}`}>{c.status}</div>
                  <div className="font-plex-mono text-[17px] font-semibold mt-1">{fmtNum(c.lines)}</div>
                  <div className={`font-plex-mono text-[10.5px] ${active ? 'text-carbon-gray-30' : 'text-carbon-gray-50'}`}>{fmtNum(c.units)} u · {fmtMoney(c.value)}</div>
                </button>
              );
            })}
          </div>

          <div className="bg-carbon-white border border-carbon-border p-3 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 xl:grid-cols-10 gap-2 items-center">
            <input aria-label="Customer" placeholder="Customer" className={inputCls} value={filters.customer} onChange={(e) => setFilter('customer', e.target.value)} />
            <input aria-label="Order or PO" placeholder="Order / PO" className={inputCls} value={filters.order} onChange={(e) => setFilter('order', e.target.value)} />
            <input aria-label="Style" placeholder="Style" className={inputCls} value={filters.style} onChange={(e) => setFilter('style', e.target.value)} />
            <select aria-label="Warehouse" className={inputCls} value={warehouse} onChange={(e) => setFilter('warehouse', e.target.value)}>
              <option value="">All warehouses</option>
              {((data && data.options && data.options.warehouses) || []).map((w) => <option key={w} value={w}>{w}</option>)}
            </select>
            <select aria-label="Division" className={inputCls} value={division} onChange={(e) => setFilter('division', e.target.value)}>
              <option value="">All divisions</option>
              {((data && data.options && data.options.divisions) || []).map((d) => <option key={d} value={d}>{d}</option>)}
            </select>
            <select aria-label="Status" className={inputCls} value={status} onChange={(e) => setFilter('status', e.target.value)}>
              <option value="">All statuses</option>
              {((data && data.statusBuckets) || []).map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
            {/* CHA-655 — a range on the order's start date (Ron's ruling); setting the end shows the invoice outlook. */}
            <input type="date" aria-label="Start from" title="Order start date from" className={inputCls} value={startFrom} max={startTo || undefined} onChange={(e) => setFilter('startFrom', e.target.value)} />
            <input type="date" aria-label="Start to" title="Order start date to" className={inputCls} value={startTo} min={startFrom || undefined} onChange={(e) => setFilter('startTo', e.target.value)} />
            <label className="flex items-center gap-2 text-xs text-carbon-gray-70">
              <input type="checkbox" checked={attention} onChange={(e) => setFilter('attention', e.target.checked)} />
              Needs attention
            </label>
            {hasFilters ? (
              <button type="button" onClick={clearFilters} className="text-xs font-medium text-carbon-blue-60 hover:text-carbon-blue-70 justify-self-start">
                Clear filters
              </button>
            ) : <span />}
          </div>

          {rangeInverted ? (
            <div role="alert" data-testid="range-inverted" className="bg-carbon-white border border-carbon-border px-3 py-3 text-[12.5px] text-carbon-gray-70">
              Start from ({startFrom}) is after Start to ({startTo}). Fix the range to load lines.
            </div>
          ) : null}

          {waterfall && !rangeInverted ? (
            <InvoiceWaterfall
              waterfall={waterfall}
              excluded={excludedSteps}
              onToggle={(key) => setExcludedSteps((prev) => {
                const next = new Set(prev);
                if (next.has(key)) next.delete(key); else next.add(key);
                return next;
              })}
              onIncludeAll={() => setExcludedSteps(new Set())}
            />
          ) : null}

          {rangeInverted ? null : (
          <div className="bg-carbon-white border border-carbon-border">
            {loading && rows.length === 0 ? (
              <SkeletonRows rows={6} />
            ) : rows.length === 0 ? (
              <NoResults query={filters.customer || filters.order || filters.style} onClear={hasFilters ? clearFilters : undefined} />
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full min-w-[1180px] text-[12px]">
                  <thead>
                    <tr className="font-plex-mono text-[9.5px] tracking-[0.1em] uppercase text-carbon-gray-50 border-b border-carbon-border">
                      {['Order / line', 'Customer', 'Style / color', 'WH', 'Units', 'Value', 'Start', 'Ready', 'Ship month', 'Status', 'Supply'].map((h) => (
                        <th key={h} className="text-left font-normal px-3 py-2 whitespace-nowrap">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r) => (
                      <tr
                        key={`${r.orderNo}|${r.lineSeq}`}
                        tabIndex={0}
                        onClick={() => setSelected(r)}
                        onKeyDown={(e) => { if (e.key === 'Enter') setSelected(r); }}
                        className="border-b border-carbon-border last:border-b-0 hover:bg-carbon-gray-10 cursor-pointer"
                      >
                        <td className="px-3 py-2 font-plex-mono whitespace-nowrap">
                          {r.attention ? <span className="inline-block w-1.5 h-1.5 bg-carbon-red-60 mr-1.5 align-middle" title="Needs attention" /> : null}
                          {r.orderNo}<span className="text-carbon-gray-50">/{r.lineSeq}</span>
                        </td>
                        <td className="px-3 py-2 whitespace-nowrap max-w-[200px] truncate" title={r.customerName || r.customer || ''}>{r.customerName || r.customer || '—'}</td>
                        <td className="px-3 py-2 font-plex-mono whitespace-nowrap">{r.style || '—'}<span className="text-carbon-gray-50"> {r.color || ''}</span></td>
                        <td className="px-3 py-2 font-plex-mono whitespace-nowrap">{r.warehouse || '—'}</td>
                        <td className="px-3 py-2 font-plex-mono text-right whitespace-nowrap">{fmtNum(r.qty)}</td>
                        <td className="px-3 py-2 font-plex-mono text-right whitespace-nowrap">{fmtMoney(r.lineValue)}</td>
                        <td className="px-3 py-2 font-plex-mono whitespace-nowrap">{r.startDate || '—'}</td>
                        <td className="px-3 py-2 font-plex-mono whitespace-nowrap">{r.readyDate || '—'}</td>
                        <td className="px-3 py-2 font-plex-mono whitespace-nowrap">{r.projectedShipMonth || '—'}</td>
                        <td className="px-3 py-2 whitespace-nowrap">{r.status}</td>
                        <td className="px-3 py-2 font-plex-mono whitespace-nowrap max-w-[260px] truncate" title={supplySummary(r.supplySources)}>{supplySummary(r.supplySources)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            <div className="flex items-center justify-between px-3 py-2 border-t border-carbon-border font-plex-mono text-[11px] text-carbon-gray-50">
              <span data-testid="page-range">
                {total === 0 ? '0 lines' : `${fmtNum(offset + 1)}–${fmtNum(Math.min(offset + rows.length, total))} of ${fmtNum(total)} lines`}
              </span>
              <span className="flex gap-1">
                <button type="button" aria-label="Previous page" disabled={offset === 0 || loading} onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))} className="p-1 disabled:opacity-30"><ChevronLeft size={15} /></button>
                <button type="button" aria-label="Next page" disabled={offset + PAGE_SIZE >= total || loading} onClick={() => setOffset(offset + PAGE_SIZE)} className="p-1 disabled:opacity-30"><ChevronRight size={15} /></button>
              </span>
            </div>
          </div>
          )}
        </>
      ) : null}

      {loading && !data && !error ? <div className="bg-carbon-white border border-carbon-border"><SkeletonRows rows={6} /></div> : null}

      {selected ? <PreAllocationDrawer row={selected} onClose={() => setSelected(null)} /> : null}
    </div>
  );
}

function stepLabel(step, invoicePeriod) {
  if (step.kind === 'invoiced') return invoicePeriod ? `invoiced ${invoicePeriod.from} → ${invoicePeriod.to}` : 'invoiced';
  if (step.kind === 'picked' && step.routingStatus) return `picked · ${step.routingStatus}`;
  return step.status;
}

// One step's identity across responses, so a choice to leave it out survives a reload or a filter change.
export function stepKey(step) {
  return `${step.kind}|${step.status || ''}|${step.routingStatus || ''}`;
}

// CHA-655 (Ron) — the outlook's total, share and running total over only the ticked steps. A view-only
// choice made on the page; the server's own totals are unchanged.
export function selectedOutlook(steps, excluded) {
  const included = steps.filter((step) => !excluded.has(stepKey(step)));
  const value = included.reduce((sum, step) => sum + step.value, 0);
  const totals = {
    lines: included.reduce((sum, step) => sum + step.lines, 0),
    units: included.reduce((sum, step) => sum + step.units, 0),
    value,
    unvaluedLines: included.reduce((sum, step) => sum + step.unvaluedLines, 0),
  };
  // Same half-cent rule as the server: a total netting to float noise has no meaningful share.
  const hasValue = Math.abs(value) >= 0.005;
  let cumulative = 0;
  const rows = steps.map((step) => {
    if (excluded.has(stepKey(step))) return { ...step, included: false, share: null, running: null };
    cumulative += step.value;
    return { ...step, included: true, share: hasValue ? step.value / value : null, running: cumulative };
  });
  return { totals, rows, excludedCount: steps.length - included.length };
}

// CHA-654 / CHA-655 — the invoice outlook for the start-date range: Invoiced so far, then the open lines
// strongest to weakest, picked lines split by routing status. Steps arrive in the server's order; the
// page only re-adds them over the steps Ron has ticked.
function InvoiceWaterfall({ waterfall, excluded, onToggle, onIncludeAll }) {
  const { startFrom, startTo, invoicePeriod, invoiced, open, totals: serverTotals, steps } = waterfall;
  const { totals, rows, excludedCount } = selectedOutlook(steps, excluded);
  const empty = serverTotals.lines === 0;
  const range = startFrom ? `start ${startFrom} → ${startTo}` : `start ≤ ${startTo}`;
  return (
    <div className="bg-carbon-white border border-carbon-border" data-testid="invoice-waterfall">
      <div className="px-3 py-2 border-b border-carbon-border flex flex-wrap items-baseline justify-between gap-2">
        <div className="font-plex-mono text-[10px] tracking-[0.12em] uppercase text-carbon-gray-50">
          Invoice outlook · <span className="text-carbon-ink">{range}</span>
        </div>
        <div className="font-plex-mono text-[11px] text-carbon-gray-50 flex flex-wrap items-baseline gap-x-3" data-testid="waterfall-totals">
          {excludedCount > 0 ? (
            <>
              <span data-testid="waterfall-selected">
                Selected <span className="text-carbon-ink font-semibold">{fmtMoney(totals.value)}</span> of {fmtMoney(serverTotals.value)}
              </span>
              <button type="button" onClick={onIncludeAll} className="text-carbon-blue-60 hover:text-carbon-blue-70">Include all</button>
            </>
          ) : (
            <span>
              {invoiced ? `${fmtMoney(invoiced.value)} invoiced + ${fmtMoney(open.value)} open = ` : `${fmtNum(open.lines)} open lines · `}
              <span className="text-carbon-ink">{fmtMoney(serverTotals.value)}</span>
            </span>
          )}
        </div>
      </div>
      {empty ? (
        <div className="px-3 py-4 text-[12.5px] text-carbon-gray-70" data-testid="waterfall-empty">
          No invoiced or open lines for {range} with these filters.
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full min-w-[600px] text-[12px]">
            <thead>
              <tr className="font-plex-mono text-[9.5px] tracking-[0.1em] uppercase text-carbon-gray-50 border-b border-carbon-border">
                <th className="w-8 px-3 py-2"><span className="sr-only">In total</span></th>
                <th className="text-left font-normal px-3 py-2">Status</th>
                <th className="text-right font-normal px-3 py-2">Value</th>
                <th className="text-right font-normal px-3 py-2">Units</th>
                <th className="text-right font-normal px-3 py-2">% of total</th>
                <th className="text-right font-normal px-3 py-2">Cumulative</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((step) => {
                const label = stepLabel(step, invoicePeriod);
                const muted = !step.included || step.lines === 0;
                return (
                  <tr
                    key={stepKey(step)}
                    data-testid="waterfall-step"
                    data-included={step.included}
                    className={`border-b border-carbon-border last:border-b-0 ${muted ? 'text-carbon-gray-50' : ''} ${step.kind === 'invoiced' && step.included ? 'font-semibold' : ''}`}
                  >
                    <td className="px-3 py-1.5">
                      <input type="checkbox" aria-label={`Include ${label} in total`} checked={step.included} onChange={() => onToggle(stepKey(step))} />
                    </td>
                    <td className={`px-3 py-1.5 whitespace-nowrap ${step.included ? '' : 'line-through'}`}>{label}</td>
                    <td className="px-3 py-1.5 font-plex-mono text-right whitespace-nowrap">{fmtMoney(step.value)}</td>
                    <td className="px-3 py-1.5 font-plex-mono text-right whitespace-nowrap">{fmtNum(step.units)}</td>
                    <td className="px-3 py-1.5 font-plex-mono text-right whitespace-nowrap">{fmtPct(step.share)}</td>
                    <td className="px-3 py-1.5 font-plex-mono text-right whitespace-nowrap">{step.running === null ? '—' : fmtMoney(step.running)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      {!empty && totals.unvaluedLines > 0 ? (
        <div className="px-3 py-2 border-t border-carbon-border text-[11.5px] text-carbon-gray-70" data-testid="waterfall-unvalued">
          {fmtNum(totals.unvaluedLines)} of {fmtNum(totals.lines)} lines have no stored value and count as $0 here.
        </div>
      ) : null}
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="bg-carbon-white px-3.5 py-2.5 min-w-0">
      <div className="font-plex-mono text-[9.5px] tracking-[0.1em] text-carbon-gray-50 uppercase">{label}</div>
      <div className="font-plex-mono text-[14px] font-semibold mt-1 break-words">{value}</div>
    </div>
  );
}

function PreAllocationDrawer({ row, onClose }) {
  const closeRef = useRef(null);
  useEffect(() => {
    const prev = document.activeElement;
    if (closeRef.current) closeRef.current.focus();
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
      if (prev && typeof prev.focus === 'function') prev.focus();
    };
  }, [onClose]);

  const explanation = STATUS_EXPLANATIONS[row.statusBucket] || STATUS_EXPLANATIONS[row.status] || '';
  return (
    <div className="fixed inset-0 z-50 flex justify-end font-plex text-carbon-ink">
      <div className="absolute inset-0 bg-[rgba(22,22,22,0.5)]" onClick={onClose} />
      <div role="dialog" aria-modal="true" aria-label={`Order line ${row.orderNo}/${row.lineSeq}`} className="relative w-full max-w-[480px] bg-carbon-gray-10 h-full flex flex-col">
        <div className="px-6 py-[18px] border-b border-carbon-border bg-carbon-white flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="font-plex-mono text-[10px] tracking-[0.14em] text-carbon-gray-50">ORDER LINE</div>
            <h2 className="font-plex-mono text-[20px] font-semibold mt-1 break-all">{row.orderNo}/{row.lineSeq}</h2>
            <div className="text-[13px] text-carbon-gray-70 mt-0.5">{row.customerName || row.customer || ''}{row.customerPo ? ` · PO ${row.customerPo}` : ''}</div>
          </div>
          <button ref={closeRef} type="button" onClick={onClose} aria-label="Close" className="text-carbon-gray-50 hover:text-carbon-ink p-1"><X size={19} /></button>
        </div>
        <div className="flex-1 overflow-y-auto px-6 py-5 flex flex-col gap-4">
          <div className="bg-carbon-white border border-carbon-border px-4 py-3">
            <div className="font-plex-mono text-[10px] tracking-[0.12em] text-carbon-gray-50 uppercase">Status</div>
            <div className="text-[15px] font-semibold mt-1" data-testid="drawer-status">{row.status}</div>
            <div className="text-[12.5px] text-carbon-gray-70 mt-1">{explanation}</div>
            <div className="text-[11px] text-carbon-gray-50 mt-1.5">Start and cancel dates are not stored in the snapshot.</div>
          </div>
          <div className="grid grid-cols-2 gap-px bg-carbon-border border border-carbon-border">
            <Stat label="Ready date" value={row.readyDate || '—'} />
            <Stat label="Ship month" value={row.projectedShipMonth || '—'} />
            <Stat label="Units" value={fmtNum(row.qty)} />
            <Stat label="Line value" value={fmtMoney(row.lineValue)} />
            <Stat label="Allocated" value={fmtNum(row.allocatedQty)} />
            <Stat label="Short" value={fmtNum(row.shortQty)} />
            <Stat label="Warehouse" value={row.warehouse || '—'} />
            <Stat label="Demand" value={row.demandLayer || '—'} />
          </div>
          {row.flaggedProjection || row.flags.length ? (
            <div className="bg-carbon-white border border-carbon-border px-4 py-3 text-[12.5px]" data-testid="drawer-flags">
              {row.flaggedProjection ? <div className="font-semibold">Projected date — evidence is a projection, not a booked date.</div> : null}
              {row.flags.map((f) => <div key={f}>Flag: {f}</div>)}
            </div>
          ) : null}
          <div>
            <div className="font-plex-mono text-[10px] tracking-[0.12em] text-carbon-gray-50 uppercase mb-2">Supply evidence</div>
            {row.supplySources.length === 0 ? (
              <div className="text-[12.5px] text-carbon-gray-70 bg-carbon-white border border-carbon-border px-4 py-3">No supply was allocated to this line.</div>
            ) : (
              <div className="flex flex-col gap-2">
                {row.supplySources.map((s, i) => (
                  <div key={i} className="bg-carbon-white border border-carbon-border px-4 py-3 text-[12.5px]" data-testid="supply-source">
                    {s.type === 'stock' ? (
                      <div><span className="font-semibold">Stock</span> · {fmtNum(s.qty)} units · ready {s.readyDate || '—'} (handling time)</div>
                    ) : (
                      <>
                        <div><span className="font-semibold font-plex-mono">PO {s.po || '—'}{s.poLine ? `/${s.poLine}` : ''}</span> · {s.stage || '—'} · {fmtNum(s.qty)} units</div>
                        <div className="grid grid-cols-2 gap-x-4 gap-y-0.5 mt-1.5 font-plex-mono text-[11.5px] text-carbon-gray-70">
                          <span>Booked: {s.booked ? 'yes' : 'no'}</span>
                          <span>Container: {s.container || '—'}</span>
                          <span>ETD: {s.etd || '—'}</span>
                          <span>Booking ETA: {s.bookingEta || '—'}</span>
                          <span>Container ETA: {s.containerEta || '—'}</span>
                          <span>Origin: {s.countryOfOrigin || '—'}</span>
                          <span>Ship date: {s.shipDate || '—'}</span>
                          <span>Due date: {s.dueDate || '—'}</span>
                        </div>
                        <div className="mt-1.5 text-[12px]">
                          Ready {s.readyDate || '—'} — basis <span className="font-plex-mono">{s.basis || '—'}</span>
                          {s.ladderStep !== undefined && s.ladderStep !== null ? <> (step {s.ladderStep})</> : null}
                          {s.flags && s.flags.length ? <> · {s.flags.join(', ')}</> : null}
                        </div>
                      </>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
